import type { HomeScreenTaskSuggestion } from '@frontend/react-domain';
import { mapSuggestion } from '@frontend/react-domain';
import { useDismissedSuggestionsSlice } from '@frontend/redux';
import { Icon, Text, XStack, YStack } from '@frontend/web-react';
import { useAnalytics, useListSuggestionsQuery, useScrollButtons } from '@frontend/web/hooks';
import { useRouter } from '@frontend/web/utils';
import { useMemo } from 'react';
import { SuggestionCard } from './SuggestionCard';

const DEFAULT_CARD_WIDTH = 320;
const DEFAULT_CARD_GAP = 16;

type SuggestionsProps = {
  cardGap?: number;
  cardWidth?: number;
};

export const Suggestions = ({ cardGap = DEFAULT_CARD_GAP, cardWidth = DEFAULT_CARD_WIDTH }: SuggestionsProps) => {
  const { navigate } = useRouter();
  const { track } = useAnalytics();
  const { dismissedSuggestions, dismissSuggestion } = useDismissedSuggestionsSlice();
  const { data: suggestionsData, isLoading } = useListSuggestionsQuery();

  const suggestions = useMemo(
    () =>
      (suggestionsData ?? [])
        .map(mapSuggestion)
        .filter((s) => !dismissedSuggestions.includes(s.suggestionId))
        .slice(0, 6),
    [suggestionsData, dismissedSuggestions],
  );

  const { canScrollLeft, canScrollRight, scrollContainerRef, scrollLeft, scrollRight, updateScrollButtons } =
    useScrollButtons({ scrollAmount: cardWidth + cardGap });

  const handleSuggestionPress = (suggestion: HomeScreenTaskSuggestion) => {
    track({
      data: { suggestion: suggestion.draftDescription, suggestionId: suggestion.suggestionId },
      event: 'suggestionSelected',
      topic: 'suggestions',
    });

    navigate({
      search: { description: suggestion.draftDescription, suggestionId: suggestion.suggestionId },
      to: '/app/draft-task/create',
    });
  };

  const handleRemoveSuggestion = (suggestion: HomeScreenTaskSuggestion, event: React.MouseEvent) => {
    event.stopPropagation();
    track({
      data: { suggestionId: suggestion.suggestionId },
      event: 'suggestionDismissed',
      topic: 'suggestions',
    });
    dismissSuggestion(suggestion.suggestionId);
  };

  if (!suggestions.length || isLoading) {
    return null;
  }

  return (
    <YStack accessibilityLabel="Task suggestions" className="gap-md">
      <XStack className="items-center justify-between">
        <Text color="primary" type="p-sm">
          Suggested for you
        </Text>
      </XStack>
      <div className="relative">
        <div
          className="flex gap-md overflow-x-auto no-scroll-bar scroll-smooth"
          onScroll={updateScrollButtons}
          ref={scrollContainerRef}
          style={{ msOverflowStyle: 'none', scrollbarWidth: 'none' }}
        >
          {suggestions.map((item) => (
            <SuggestionCard
              key={item.suggestionId}
              onPress={() => handleSuggestionPress(item)}
              onRemove={(e) => handleRemoveSuggestion(item, e)}
              suggestion={item}
              width={cardWidth}
            />
          ))}
        </div>

        {canScrollLeft ? (
          <button
            aria-label="Scroll left"
            className="absolute left-none top-1/2 -translate-y-1/2 -translate-x-1/2 w-xxl h-xxl bg-cream-100 rounded-full flex items-center justify-center hover:bg-slate-50 transition-colors z-10 border-1 border-borderTertiary"
            onClick={scrollLeft}
            type="button"
          >
            <Icon color="primary" name="IconChevronLeft" size="md" />
          </button>
        ) : null}

        {canScrollRight && suggestions.length > 3 ? (
          <button
            aria-label="Scroll right"
            className="absolute right-none top-1/2 -translate-y-1/2 translate-x-1/2 w-xxl h-xxl bg-cream-100 rounded-full flex items-center justify-center hover:bg-slate-50 transition-colors z-10 border-1 border-borderTertiary"
            onClick={scrollRight}
            type="button"
          >
            <Icon color="primary" name="IconChevronRight" size="md" />
          </button>
        ) : null}
      </div>
    </YStack>
  );
};
